import React, { forwardRef, useEffect, useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { ProgressCircularDarkDanger } from '../../../icons/progressCircular/ProgressCircularDarkDanger'
import { ProgressCircularDarkDefault } from '../../../icons/progressCircular/ProgressCircularDarkDefault'
import { ProgressCircularDarkSuccess } from '../../../icons/progressCircular/ProgressCircularDarkSuccess'
import { ProgressCircularLightDanger } from '../../../icons/progressCircular/ProgressCircularLightDanger'
import { ProgressCircularLightDefault } from '../../../icons/progressCircular/ProgressCircularLightDefault'
import { ProgressCircularLightSuccess } from '../../../icons/progressCircular/ProgressCircularLightSuccess'
import { pxToRem } from '../../../theme'
import { ButtonProminence } from '../Button/ArcadeButton'

export type Size = 'xsmall' | 'small' | 'medium' | 'large'
type Variant = 'default' | 'success' | 'danger'

type AnimationAssets = {
  light: React.ComponentType<{ ariaLabel: string; dataTestId: string }>
  dark: React.ComponentType<{ ariaLabel: string; dataTestId: string }>
}

export type ProgressCircularProps = {
  size?: Size
  showSuccess?: boolean
  dataTestId?: string
  loadingAriaLabel?: string
  successAriaLabel?: string
  isDestructive?: boolean
  buttonProminence?: ButtonProminence
}

const Container = styled.div<{ size: string }>`
  position: relative;
  width: ${props => props.size};
  height: ${props => props.size};
  overflow: visible;
`

const AnimatedContainer = styled.div<{ isVisible: boolean; isSuccess?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: center center;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transform: ${props => {
    if (props.isSuccess) {
      return props.isVisible ? 'scale(1)' : 'scale(0.6)'
    }
    return props.isVisible ? 'scale(1) rotate(0deg)' : 'scale(0.6) rotate(0deg)'
  }};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`

const SvgWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

export const ProgressCircular = forwardRef<HTMLDivElement, ProgressCircularProps>(
  (
    {
      size = 'medium',
      showSuccess = false,
      dataTestId = 'progressCircular',
      loadingAriaLabel = 'Loading',
      successAriaLabel = 'Success',
      isDestructive = false,
      buttonProminence,
    },
    ref
  ) => {
    const theme = useTheme()
    const [variant, setVariant] = useState<Variant>('default')

    useEffect(() => {
      if (showSuccess) {
        setVariant('success')
      } else {
        setVariant('default')
      }
    }, [showSuccess])

    const getSizeValue = (size: Size): string => {
      switch (size) {
        case 'xsmall':
          return `${pxToRem(16)}`
        case 'small':
          return `${pxToRem(24)}`
        case 'large':
          return `${pxToRem(56)}`
        default:
        case 'medium':
          return `${pxToRem(32)}`
      }
    }

    const getVariantAnimation = (variant: Variant): AnimationAssets => {
      switch (variant) {
        case 'success':
          return {
            light: ProgressCircularLightSuccess,
            dark: ProgressCircularDarkSuccess,
          }
        case 'danger':
          return {
            light: ProgressCircularLightDanger,
            dark: ProgressCircularDarkDanger,
          }
        default:
          return {
            light: ProgressCircularLightDefault,
            dark: ProgressCircularDarkDefault,
          }
      }
    }

    const defaultVariant = getVariantAnimation('default')
    const successVariant = getVariantAnimation('success')
    const dangerVariant = getVariantAnimation('danger')

    const currentSize = getSizeValue(size)
    const ariaLabel = variant === 'default' ? loadingAriaLabel : successAriaLabel

    const SuccessComponent = theme?.darkMode ? successVariant.dark : successVariant.light

    let LoadingComponent
    switch (buttonProminence) {
      case 'prominent':
        LoadingComponent = theme?.darkMode ? defaultVariant.light : defaultVariant.dark
        break
      case 'standard':
      case 'subtle':
        if (isDestructive) {
          LoadingComponent = theme?.darkMode ? dangerVariant.dark : dangerVariant.light
        } else {
          LoadingComponent = theme?.darkMode ? defaultVariant.dark : defaultVariant.light
        }
        break
      default:
        LoadingComponent = theme?.darkMode ? defaultVariant.dark : defaultVariant.light
        break
    }

    /** We have two separate explicit SVGs so we can control fade in and fade out animations for the loading and success SVGs. */
    return (
      <Container size={currentSize} data-testid={dataTestId} role="img" aria-label={ariaLabel} ref={ref}>
        <AnimatedContainer isVisible={variant === 'default'} aria-hidden>
          <SvgWrapper>
            <LoadingComponent ariaLabel={ariaLabel} dataTestId={`${dataTestId}-default`} />
          </SvgWrapper>
        </AnimatedContainer>
        <AnimatedContainer isVisible={variant === 'success'} isSuccess={true} aria-hidden>
          <SvgWrapper>
            <SuccessComponent ariaLabel={ariaLabel} dataTestId={`${dataTestId}-success`} />
          </SvgWrapper>
        </AnimatedContainer>
      </Container>
    )
  }
)

ProgressCircular.displayName = 'ProgressCircular'

export default ProgressCircular
